// src/pages/home/Home.tsx
//
// Home v2 (mockup `bluelt-home-mockup-v2`). Ordem da página:
//   1. Resumo em linguagem natural + atalho para a fila de aprovação
//   2. Alçada de aprovação do próprio usuário (quando disponível)
//   3. Card de pendências (contagens que espelham colunas de Kanban)
//   4. Cards de módulos — bloco PRESERVADO do estado anterior, movido para o fim
//
// REGRA TRANSVERSAL: nada com valor zero aparece — linha, bloco ou informação.
// Um erro, porém, NÃO é zero: ele aparece como '—'. Transformar falha de rede em
// "tudo em dia" numa fila de aprovação é a mentira mais cara desta tela.
//
// Blocos do mockup deliberadamente FORA (sem fonte de dado real no backend):
// painel de KPIs, sparklines, deltas percentuais, busca Ctrl+K e barras de
// uso-vs-limite. Banner de contrato suspenso/bloqueado também fora — o
// interceptor de `api.ts` derruba a sessão nesses estados, então o banner nunca
// renderizaria. Lição da ADR-007: código escrito para uso futuro sem plano
// concreto é peso morto.
import { Fragment, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faClipboardList,
  faCartShopping,
  faBoxesStacked,
  faTruckFast,
  faListCheck,
  faChartSimple,
  faChartPie,
  faGears,
  faDatabase,
  faUsers,
  faFileContract,
  faFileSignature,
  faBoxOpen,
  faBolt,
  faArrowRight,
  faCheck,
  faBan,
} from '@/icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlement } from '../../contexts/EntitlementContext';
import { Toast } from '../../contexts/ToastContext';
import { useHomePendencias, type PendenciaKey } from '@/hooks/useHomePendencias';
import { useMinhaAlcada } from '@/hooks/useMinhaAlcada';
import {
  resumirCategorias,
  devePendenciaAparecer,
  devePrecoMaximoAparecer,
  estaTudoEmDia,
} from './homeView';
import '../../pages/home/Home.css';
import { logger } from '@/utils/logger';

interface CardInfo {
  label: string;
  icon: IconDefinition;
  permission: string;
  route: string;
}

// Apresentação de cada pendência. Os NÚMEROS vêm de `useHomePendencias`; aqui
// vive só o texto, o ícone, a severidade e o destino — de propósito, para que
// uma futura troca da origem dos dados não toque em nada visual.
// `titulo` recebe `isMaster` porque o possessivo muda de sentido: MASTER vê o
// total do tenant, não "as suas" pendências — "aguardando SUA aprovação" seria
// falso para ele.
const PENDENCIAS_UI: Record<
  PendenciaKey,
  {
    titulo: (isMaster: boolean) => string;
    icon: IconDefinition;
    severidade: 'alta' | 'media' | 'info';
    tag: string;
    cta: string;
    rota: string;
  }
> = {
  aprovacao: {
    titulo: (isMaster) => (isMaster ? 'Requisições aguardando aprovação' : 'Requisições aguardando sua aprovação'),
    icon: faFileSignature,
    severidade: 'alta',
    tag: 'Urgente',
    cta: 'Aprovar',
    rota: '/requisicoes',
  },
  recebimento: {
    titulo: () => 'Ordens de compra pendentes de recebimento',
    icon: faBoxOpen,
    severidade: 'media',
    tag: 'Atenção',
    cta: 'Conferir',
    rota: '/conferencia',
  },
  compras: {
    // "Solicitações", não "Ordens": a contagem vem da coluna "Requisições
    // Pendentes" de /compras, que são pedidos aguardando virar ordem de compra.
    titulo: () => 'Solicitações de compra a processar',
    icon: faCartShopping,
    severidade: 'info',
    tag: 'Na fila',
    cta: 'Processar',
    rota: '/compras',
  },
};

// Frase do resumo em linguagem natural, partida em duas: `destaque` (número +
// substantivo) recebe a cor de ênfase, `resto` fica no texto corrido. Ex.:
// "Você tem [3 requisições] aguardando sua aprovação e [12 ordens de compra]
// pendentes de recebimento."
const RESUMO_FRASE: Record<
  PendenciaKey,
  (n: number, isMaster: boolean) => { destaque: string; resto: string }
> = {
  aprovacao: (n, isMaster) => ({
    destaque: `${n} ${n === 1 ? 'requisição' : 'requisições'}`,
    resto: isMaster ? 'aguardando aprovação' : 'aguardando sua aprovação',
  }),
  recebimento: (n) => ({
    destaque: `${n} ${n === 1 ? 'ordem' : 'ordens'} de compra`,
    resto: `pendente${n === 1 ? '' : 's'} de recebimento`,
  }),
  compras: (n) => ({
    destaque: `${n} ${n === 1 ? 'solicitação' : 'solicitações'} de compra`,
    resto: 'a processar',
  }),
};

const ORDEM_PENDENCIAS: PendenciaKey[] = ['aprovacao', 'recebimento', 'compras'];

// Mesmo padrão de `AprovacaoItemsSidePanelContent`/`Compras`/`Requisicoes`.
const formatBRL = (v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const Home = () => {
  const { theme } = useTheme();
  const { permissoes, role, isInitialized, hasPermission } = useAuth();
  const { isAreaEnabled } = useEntitlement();
  const navigate = useNavigate();
  const pendencias = useHomePendencias();
  const { dados: alcada } = useMinhaAlcada();

  // ROLE_MASTER tem bypass: vê o total do tenant, não "as suas" pendências, e
  // não tem limite de alçada/categoria/produto para exibir.
  const isMaster = role === 'ROLE_MASTER';

  // Exibe um card = (usuário tem a permissão) E (tenant tem o módulo da área).
  // O eixo de módulo (isAreaEnabled) NÃO tem bypass de ROLE_MASTER. A área é o
  // sufixo da permissão `modulo.<area>` (ex.: 'requisicoes', 'gestao_compras').
  const canShowCard = (card: CardInfo): boolean =>
    hasPermission(card.permission) && isAreaEnabled(card.permission.split('.')[1]);

  useEffect(() => {
    document.title = 'Bluelt | Home';
    logger.log('Home mounted - isInitialized:', isInitialized, 'permissoes:', permissoes, 'role:', role);
  }, [permissoes, role, isInitialized]);

  const cardsInfo: CardInfo[] = [
    { label: 'Requisições', icon: faClipboardList, permission: 'modulo.requisicoes', route: '/requisicoes' },
    { label: 'Compras', icon: faCartShopping, permission: 'modulo.gestao_compras', route: '/compras' },
    { label: 'Planejamento', icon: faBoxesStacked, permission: 'modulo.planejamento', route: '' },
    { label: 'Distribuição', icon: faTruckFast, permission: 'modulo.gestao_transferencias', route: '' },
    { label: 'Conferência', icon: faListCheck, permission: 'modulo.gestao_conferencias', route: '/conferencia' }, // Ajustado para nova permissão
    { label: 'Blue B.I.', icon: faChartSimple, permission: 'modulo.bluebi', route: '/bluebi' },
    { label: 'Relatórios', icon: faChartPie, permission: 'modulo.relatorios', route: '' },
    { label: 'Parametrização', icon: faGears, permission: 'modulo.parametrizacao', route: '/parametrizacao' },
    { label: 'Integrações', icon: faDatabase, permission: 'modulo.integracoes', route: '/integracoes' },
    { label: 'Usuários', icon: faUsers, permission: 'modulo.gestao_usuarios', route: '/usuarios' },
    { label: 'Contrato Bluelt', icon: faFileContract, permission: 'modulo.contrato_bluelt', route: '/contrato' },
  ];

  /*const sortAlphabetically = (items: typeof cardsInfo) =>
    [...items].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));*/

  const handleCardClick = (route: string, label: string) => {
    if (route) {
      navigate(route);
    } else {
      Toast.info(`Ainda estamos desenvolvendo ${label}`);
    }
  };

  // --- Pendências -----------------------------------------------------------
  const contagens = ORDEM_PENDENCIAS.map((chave) => pendencias[chave]);
  // O card só existe se o usuário tem acesso a ALGUMA pendência. Sem acesso a
  // nenhuma, a Home mostra apenas os módulos.
  const temAlgumaPendenciaAcessivel = contagens.some((c) => c !== null);
  const visiveis = ORDEM_PENDENCIAS.filter((chave) => devePendenciaAparecer(pendencias[chave]));
  const tudoEmDia = estaTudoEmDia(contagens);

  // Soma apenas o que chegou com sucesso — não inventa total quando algo falhou.
  const totalPendencias = ORDEM_PENDENCIAS.reduce((acc, chave) => {
    const c = pendencias[chave];
    return c?.status === 'ok' ? acc + (c.total ?? 0) : acc;
  }, 0);

  const aprovacao = pendencias.aprovacao;
  const totalAprovacao = aprovacao?.status === 'ok' ? aprovacao.total ?? 0 : 0;

  // Resumo em linguagem natural: cita APENAS contadores acima de zero.
  const frasesResumo = ORDEM_PENDENCIAS.reduce<
    { chave: PendenciaKey; destaque: string; resto: string }[]
  >((acc, chave) => {
    const c = pendencias[chave];
    if (c?.status === 'ok' && (c.total ?? 0) > 0) {
      acc.push({ chave, ...RESUMO_FRASE[chave](c.total as number, isMaster) });
    }
    return acc;
  }, []);

  // --- Alçada ---------------------------------------------------------------
  const categorias = alcada ? resumirCategorias(alcada.categoriasPermitidas) : null;
  const mostraPrecoMaximo = !isMaster && devePrecoMaximoAparecer(alcada?.precoMaximo);
  const qtdProdutosExcluidos = alcada?.produtosExcluidos?.length ?? 0;
  const mostraAlcada = !isMaster && (mostraPrecoMaximo || categorias !== null || qtdProdutosExcluidos > 0);

  return (
    <div className={`home-container ${theme}`}>
      {/* 1. RESUMO — só renderiza quando há ao menos um contador acima de zero */}
      {frasesResumo.length > 0 && (
        <section className="home-summary">
          <div className="home-summary-icon">
            <FontAwesomeIcon icon={faBolt} />
          </div>
          <p className="home-summary-text">
            {isMaster ? 'O tenant tem ' : 'Você tem '}
            {frasesResumo.map((frase, i) => (
              <Fragment key={frase.chave}>
                {i > 0 && (i === frasesResumo.length - 1 ? ' e ' : ', ')}
                <span className="home-summary-k">{frase.destaque}</span> {frase.resto}
              </Fragment>
            ))}
            .
          </p>
          {totalAprovacao > 0 && (
            <Link to="/requisicoes" className="home-btn-primary">
              Abrir fila de aprovação <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          )}
        </section>
      )}

      {/* 2. ALÇADA — não se aplica a ROLE_MASTER (sem limites) e some em 403 */}
      {mostraAlcada && (
        <section className="home-alcada">
          {mostraPrecoMaximo && (
            <div className="home-alcada-bloco">
              <div className="home-alcada-label">Você pode aprovar até</div>
              <div className="home-alcada-valor">{formatBRL(alcada?.precoMaximo)}</div>
            </div>
          )}
          {categorias && (
            <div className="home-alcada-bloco">
              <div className="home-alcada-label">Categorias permitidas</div>
              <div className="home-chips">
                {categorias.exibidas.map((nome) => (
                  <span className="home-chip" key={nome}>
                    <FontAwesomeIcon icon={faCheck} /> {nome}
                  </span>
                ))}
                {categorias.ocultas > 0 && (
                  <span className="home-chip home-chip-resto">e outras {categorias.ocultas}</span>
                )}
              </div>
            </div>
          )}
          {qtdProdutosExcluidos > 0 && (
            <div className="home-alcada-bloco">
              <div className="home-alcada-label">Restrições</div>
              <div className="home-chips">
                <span className="home-chip home-chip-off">
                  <FontAwesomeIcon icon={faBan} /> {qtdProdutosExcluidos}{' '}
                  {qtdProdutosExcluidos === 1 ? 'produto excluído' : 'produtos excluídos'}
                </span>
              </div>
            </div>
          )}
        </section>
      )}

      {/* 3. PENDÊNCIAS */}
      {temAlgumaPendenciaAcessivel && (
        <section className="home-card" aria-labelledby="home-pend-titulo">
          <div className="home-card-head">
            <h2 id="home-pend-titulo">{isMaster ? 'Pendências totais' : 'Minhas pendências'}</h2>
            {!pendencias.isLoading && totalPendencias > 0 && (
              <span className="home-count-pill">{totalPendencias}</span>
            )}
          </div>

          {pendencias.isLoading ? (
            // Skeleton do card INTEIRO: revelar linhas e ir apagando os zeros
            // conforme as respostas chegam produziria conteúdo que pisca.
            <div className="home-skeleton" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div className="home-skeleton-row" key={i}>
                  <div className="home-skel home-skel-icon" />
                  <div className="home-skel home-skel-num" />
                  <div className="home-skeleton-lines">
                    <div className="home-skel home-skel-line" />
                    <div className="home-skel home-skel-line short" />
                  </div>
                </div>
              ))}
            </div>
          ) : tudoEmDia ? (
            <div className="home-empty">
              <div className="home-empty-emoji">✨</div>
              <h3>Tudo em dia</h3>
              {/* Sem possessivo para MASTER: o card dele mostra o total do
                  tenant, então "para você" seria incoerente com o título. */}
              <p>
                {isMaster
                  ? 'Nenhuma pendência no tenant agora. Use os módulos abaixo quando precisar.'
                  : 'Nenhuma pendência para você agora. Use os módulos abaixo quando precisar.'}
              </p>
            </div>
          ) : (
            <ul className="home-tasks">
              {visiveis.map((chave) => {
                const ui = PENDENCIAS_UI[chave];
                const contagem = pendencias[chave];
                const emErro = contagem?.status === 'error';
                return (
                  <li className="home-task" data-sev={ui.severidade} key={chave}>
                    <div className="home-task-icon">
                      <FontAwesomeIcon icon={ui.icon} />
                    </div>
                    <div className="home-task-num">{emErro ? '—' : contagem?.total}</div>
                    <div className="home-task-body">
                      <p className="home-task-title">{ui.titulo(isMaster)}</p>
                      <p className="home-task-meta">
                        {emErro ? (
                          <span className="home-task-erro">Não foi possível carregar esta contagem</span>
                        ) : (
                          <span className="home-sev-tag">{ui.tag}</span>
                        )}
                      </p>
                    </div>
                    <Link to={ui.rota} className="home-task-cta">
                      {ui.cta} <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* 4. MÓDULOS — bloco preservado; `cardsInfo` segue a fonte canônica de
          descobribilidade das features (ver documentation/impact/pages-home.md) */}
      <div className="cards-wrapper">
        <div className="cards-container">
          {isInitialized ? (
            (cardsInfo.every((card) => !canShowCard(card)) ? (
              <div className="no-permissions">Nenhum módulo disponível.</div>
            ) : (
              cardsInfo
                .filter(canShowCard)
                .map((card, index) => (
                  <div
                    key={index}
                    className="card"
                    onClick={() => handleCardClick(card.route, card.label)}
                  >
                    <FontAwesomeIcon icon={card.icon} className="card-icon" />
                    <span className="card-label">{card.label}</span>
                  </div>
                ))
            ))
          ) : (
            <div><p>Carregando...</p></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
